import { Close } from "@mui/icons-material";
import { Box, ButtonBase, Card, IconButton, Popover, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

type Props = {
    disabled?: boolean;
    value: string;
    onChange: (color: string) => void;
};

export default function GaiaColorPicker({ disabled, value, onChange }: Props) {
    const { t } = useTranslation("app");
    const [textInputValue, setTextInputValue] = useState(value);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    useEffect(() => {
        setTextInputValue(value);
    }, [value]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColorChange = (newColor: string) => {
        onChange(newColor);
        setTextInputValue(newColor);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setTextInputValue(newValue);

        if (/^#([0-9A-Fa-f]{3}){1,2}$/.test(newValue)) {
            onChange(newValue);
        }
    };

    return (
        <Box>
            <Card
                component={ButtonBase}
                disabled={disabled}
                elevation={0}
                onClick={handleClick}
                sx={{
                    p: 2,
                    border: "solid 2px",
                    borderColor: "divider",
                    background: value,
                    width: "40px",
                    height: "40px",
                }}
            />

            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Stack gap={2} padding={2} alignItems="end">
                    <IconButton onClick={handleClose} sx={{ width: "40px", height: "40px" }}>
                        <Close />
                    </IconButton>

                    <HexColorPicker color={value} onChange={handleColorChange} />

                    <TextField
                        value={textInputValue}
                        onChange={handleInputChange}
                        sx={{ width: "200px" }}
                        label={t("GaiaColorPicker.hexValue")}
                    />
                </Stack>
            </Popover>
        </Box>
    );
}

type GaiaColorPickerFieldProps<T extends FieldValues> = {
    control: Control<T>;
    name: FieldPath<T>;
    label?: string;
    disabled?: boolean;
};

export function GaiaColorPickerField<T extends FieldValues>({ control, name, label, disabled }: GaiaColorPickerFieldProps<T>) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { onChange, value } }) => (
                <Stack gap={0.5}>
                    {label && (
                        <Typography variant="caption" color="text.secondary">
                            {label}
                        </Typography>
                    )}
                    <GaiaColorPicker disabled={disabled} value={value} onChange={onChange} />
                </Stack>
            )}
        />
    );
}
